import React, { useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import BondTable from "./BondTable";
import { usePortfolioRebalanceStore } from "../../Store/portfolioRebalanceStore";
import { tokens } from "../../Common/designTokens";
const OrderPreviewTable = ({ 
  data, 
  onCusipClick, 
  rebalanceFactor, 
  onTradeQtyChange, 
  tradeQtyCellRenderer,
  endingQtyCellRenderer,
  tradeValueCellRenderer,
  endingSecurityMarketValueCellRenderer,
  endingWeightCellRenderer,
  isPortfolioPlayMode,
}) => {
  // Get rating mappings from store
  const { snpRatingOptions, moodyRatingOptions } = usePortfolioRebalanceStore();
  
  // Local sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Sort the data based on current sort config
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !data) return data;

    // Helper function to get rating value for sorting
    const getRatingValue = (rating, isSnp = true) => {
      if (!rating) return Number.MAX_SAFE_INTEGER; // Put null/undefined ratings at end
      
      const ratingOptions = isSnp ? snpRatingOptions : moodyRatingOptions;
      const ratingObj = ratingOptions.find(r => r.rating === rating);
      return ratingObj ? ratingObj.value : Number.MAX_SAFE_INTEGER;
    };

    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Special handling for rating columns
      if (sortConfig.key === "spRating" || sortConfig.key === "moodyRating") {
        const isSnp = sortConfig.key === "spRating";
        const aRatingValue = getRatingValue(aValue, isSnp);
        const bRatingValue = getRatingValue(bValue, isSnp);
        
        // If both are unknown/null (MAX_SAFE_INTEGER), maintain original order
        if (aRatingValue === Number.MAX_SAFE_INTEGER && bRatingValue === Number.MAX_SAFE_INTEGER) {
          return 0;
        }
        // If only aValue is unknown/null, put it at the end
        if (aRatingValue === Number.MAX_SAFE_INTEGER) return 1;
        // If only bValue is unknown/null, put it at the end  
        if (bRatingValue === Number.MAX_SAFE_INTEGER) return -1;
        
        // For ASC: lower rating first (which means higher value in our mapping)
        // So we reverse the normal numeric comparison
        return sortConfig.direction === "asc" ? bRatingValue - aRatingValue : aRatingValue - bRatingValue;
      }

      // Handle null/undefined values for non-rating columns
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Convert to numbers if both are numeric
      const aNum = Number(aValue);
      const bNum = Number(bValue);
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      // String comparison for non-numeric values
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (sortConfig.direction === "asc") {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });

    return sorted;
  }, [data, sortConfig, snpRatingOptions, moodyRatingOptions]);

  // Handle local sorting
  const handleSort = (columnKey, direction) => {
    setSortConfig({ key: columnKey, direction });
  };

  // Map rebalance factor to relevant columns
  const rebalanceColumnsMap = {
    // ... (unchanged)
  };

  // Always show these base columns (default left-to-right order adjusted)
  const baseColumns = [
  { key: "bondType", name: "Bond Type", render: (value) => value || "---" },
  { key: "securityCharge", name: "Factored Securities", render: (value) => value || "---" },
  { key: "side", name: "Side" },
  { key: "cusip", name: "SecurityId" },
  { key: "securityDesc", name: "Security Description" },
  { key: "currentPrice", name: "Trading Price", numeric: true },
  { key: "currentQuantity", name: "Current Qty", numeric: true },
  { key: "securityWeightPercentage", name: "Current Weight %", numeric: true },
  { key: "securityMarketValue", name: "Current Market Value", numeric: true },
  { key: "endingQuantity", name: "Ending Qty", numeric: true },
  { key: "endingSecurityWeightPercentage", name: "Ending Weight %", numeric: true },
  { key: "endingSecurityMarketValue", name: "Ending Market Value", numeric: true },
  { key: "tradeQuantity", name: "Trade Qty", numeric: true },
  { key: "tradeValue", name: "Trade Value", numeric: true },
  { key: "accruedInterest", name: "Accrued Interest", numeric: true },
  { key: "maturityDate", name: "Maturity Date" },
  { key: "moodyRating", name: "Moody's Rating", render: (value) => value || "---" },
  { key: "spRating", name: "S&P Rating", render: (value) => value || "---" },
  { key: "couponPer", name: "Coupon", render: (value) => value ?? "---" },
  { key: "yield", name: "YTM", numeric: true },
  { key: "yieldToWorst", name: "YTW", numeric: true },
  { key: "dv01", name: "DV01", numeric: true },
  { key: "duration", name: "$ Duration", numeric: true },
];

  // Helper to insert rebalance columns after their similar base column
  function insertRebalanceColumns(baseCols, rebalanceCols, rebalanceFactor) {
    // Map rebalance factor to the base column key after which to insert
    const insertAfterMap = {
      Duration: "duration",
     
      Yield: "yield",
    
      DV01: "dv01",
    
      "S&P Rating": "spRating",
      "Moody's Rating": "moodyRating",
    };
    const insertAfterKey = insertAfterMap[rebalanceFactor];
    if (!insertAfterKey || !rebalanceCols || rebalanceCols.length === 0) return baseCols;

    const result = [];
    for (const col of baseCols) {
      result.push(col);
      if (col.key === insertAfterKey) {
        // Insert rebalance columns right after the similar base column
        result.push(...rebalanceCols);
      }
    }
    return result;
  }

  // Compose columns to show, inserting rebalance columns after their similar base column
  const previewColumns = insertRebalanceColumns(
    baseColumns,
    rebalanceColumnsMap[rebalanceFactor],
    rebalanceFactor
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 297px)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: tokens.fontColour,
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            {isPortfolioPlayMode ? "Portfolio Play Trades" : "Rebalance Trades"} ({data ? data.length.toLocaleString() : 0})
          </Typography>
        </Box>

        {/* Table content */}
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <BondTable
            selectedColumns={previewColumns}
            filteredData={sortedData}
            handleRowClick={() => {}}
            handleViewJson={() => {}}
            sectionKey="order-preview"
            source="order-preview"
            handleCusipClick={onCusipClick}
            loading={false}
            onTradeQtyChange={onTradeQtyChange}
            tradeQtyCellRenderer={tradeQtyCellRenderer}
            endingQtyCellRenderer={endingQtyCellRenderer}
            tradeValueCellRenderer={tradeValueCellRenderer}
            endingSecurityMarketValueCellRenderer={endingSecurityMarketValueCellRenderer}
            endingWeightCellRenderer={endingWeightCellRenderer}
            showCheckboxes={false}
            onSort={handleSort}
            sortConfig={sortConfig}
            // No selection props for preview mode
          />
        </Box>
      </Box>
    </>
  );
};

export default OrderPreviewTable;
